export const PRODUCT_TYPE_LABELS = {
  HUBBLE_GIFT_CARD: 'Hubble Gift Card',
  DUGOUT_PRODUCT: 'Dugout Merch',
};

export const ORDER_STATUS_LABELS = {
  PENDING: 'Pending',
  PAYMENT_DEBITED: 'Payment debited',
  PROCESSING: 'Processing',
  SUCCESS: 'Success',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
  CANCELLED: 'Cancelled',
  FULFILLED: 'Fulfilled',
};

export const FULFILLMENT_STATUS_LABELS = {
  NOT_REQUIRED: 'Not required',
  MANUAL_REQUIRED: 'Manual required',
  IN_PROGRESS: 'In progress',
  DELIVERED: 'Delivered',
  FAILED: 'Failed',
};

export const PAYMENT_STATUS_LABELS = {
  INITIATED: 'Initiated',
  DEBITED: 'Debited',
  REFUNDED: 'Refunded',
  FAILED: 'Failed',
};

/** Build uppercase key from category label (client-side when key omitted). */
export function slugifyCategoryKey(label) {
  const key = String(label || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return key || 'CATEGORY';
}

/** Normalize GET /admin/store/categories response. */
export function parseCategoriesList(res) {
  const payload = res?.data ?? res;
  if (Array.isArray(payload)) return payload;
  return payload?.categories || [];
}

export function getCategoryLabel(categories, key) {
  if (!key) return '—';
  const found = (categories || []).find((c) => c.key === key);
  return found?.label || key;
}

/** Resolve shipping address from order (canonical: shipping_address). */
export function getOrderAddress(order) {
  if (!order || typeof order !== 'object') return null;
  const addr = order.shipping_address || order.address || order.delivery_address;
  if (!addr || typeof addr !== 'object') return null;
  if (!addr.line1 && !addr.city) return null;
  return addr;
}

export function formatAddressLines(address) {
  if (!address) return [];
  const lines = [];
  if (address.name) lines.push(address.name);
  if (address.phone) lines.push(address.phone);
  if (address.line1) lines.push(address.line1);
  if (address.line2) lines.push(address.line2);
  const cityLine = [address.city, address.state, address.pin_code].filter(Boolean).join(', ');
  if (cityLine) lines.push(cityLine);
  if (address.gst_number) lines.push(`GST: ${address.gst_number}`);
  return lines;
}

export function formatAddressShort(address) {
  const lines = formatAddressLines(address);
  if (!lines.length) return '—';
  return lines.slice(0, 2).join(' · ');
}

/** Nested product on store order rows (GET /admin/store/orders). */
export function getOrderProduct(order) {
  if (!order?.product || typeof order.product !== 'object') return null;
  return order.product;
}

export function getOrderProductTitle(order) {
  const product = getOrderProduct(order);
  if (product?.title) return product.title;
  if (order?.store_product_id != null) return `Product #${order.store_product_id}`;
  return '—';
}

export function getOrderCustomerName(order) {
  const addr = getOrderAddress(order);
  return addr?.name?.trim() || null;
}

export function getOrderCustomerPhone(order) {
  const addr = getOrderAddress(order);
  return addr?.phone?.trim() || null;
}

export function formatOrderCustomerLabel(order) {
  const name = getOrderCustomerName(order);
  const userId = order?.user_id;
  if (name && userId) return `${name} (${userId})`;
  if (name) return name;
  if (userId) return `User ${userId}`;
  return '—';
}

/** Normalize GET /admin/store/hubble/discounts response. */
export function parseDiscountsList(res) {
  const payload = res?.data ?? res;
  if (Array.isArray(payload)) return payload;
  return payload?.discounts || [];
}

const toNumberOrNull = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/** First value that is a number greater than zero — treats 0 as "not set". */
const firstPositive = (...values) => {
  for (const value of values) {
    const num = toNumberOrNull(value);
    if (num != null && num > 0) return num;
  }
  return null;
};

/** Brand key used by PATCH /admin/store/hubble/discounts/:brandId for a catalog product. */
export function getDiscountBrandId(product) {
  return product?.hubble_product_id ?? product?.hubble_brand_id ?? product?.brand_id ?? null;
}

/** Catalog product backing a discount row, matched by product id then brand then title. */
export function findProductForDiscount(products, discount) {
  if (!discount || !Array.isArray(products)) return null;

  const productId = discount.store_product_id ?? discount.product_id ?? discount.product?.id;
  if (productId != null) {
    const byId = products.find((p) => String(p.id) === String(productId));
    if (byId) return byId;
  }

  const brandId = discount.brand_id;
  if (brandId != null) {
    const byBrand = products.find((p) => {
      const productBrandId = getDiscountBrandId(p);
      return productBrandId != null && String(productBrandId) === String(brandId);
    });
    if (byBrand) return byBrand;
  }

  const title = String(discount.store_product_title || '').trim().toLowerCase();
  if (title) {
    const byTitle = products.find((p) => String(p.title || '').trim().toLowerCase() === title);
    if (byTitle) return byTitle;
  }

  return null;
}

/** Discount row for a catalog product, matched on the brand key the discounts API uses. */
export function findDiscountForProduct(discounts, product) {
  const brandId = getDiscountBrandId(product);
  if (brandId == null || !Array.isArray(discounts)) return null;
  return discounts.find((d) => String(d.brand_id) === String(brandId)) || null;
}

/**
 * Split a gift card's face value into the Fan Energy-funded part and the cash the user still pays.
 *
 * `discountPercent` projects a split for a discount that has not been saved yet, and
 * `fanEnergyPerInr` is the global store rate used to convert the discount rupees into Fan Energy.
 * Catalog fields are only a fallback because they stay empty until a discount sync has run.
 */
export function getPricingPreview(product, { discountPercent, fanEnergyPerInr } = {}) {
  const catalogPercent = toNumberOrNull(product?.hubble_discount_percent);
  const catalogDiscountInr = toNumberOrNull(product?.hubble_discount_inr);
  const catalogPayableInr = toNumberOrNull(product?.estimated_real_money_payable_inr);
  const catalogFanEnergy = toNumberOrNull(product?.fan_energy_required_for_max_discount);

  const faceValueInr =
    firstPositive(product?.original_price_inr, product?.real_amount_inr) ??
    (catalogDiscountInr != null && catalogPayableInr != null
      ? catalogDiscountInr + catalogPayableInr
      : null);

  const requested = toNumberOrNull(discountPercent);
  const effectivePercent = requested ?? catalogPercent;
  const isProjected = requested != null && requested !== catalogPercent;

  let fanEnergyCoversInr =
    faceValueInr != null && effectivePercent != null ? (faceValueInr * effectivePercent) / 100 : null;
  if (fanEnergyCoversInr == null && !isProjected) fanEnergyCoversInr = catalogDiscountInr;

  let cashPayableInr =
    faceValueInr != null && fanEnergyCoversInr != null ? faceValueInr - fanEnergyCoversInr : null;
  if (cashPayableInr == null && !isProjected) cashPayableInr = catalogPayableInr;

  const ratePerInr =
    toNumberOrNull(fanEnergyPerInr) ??
    (catalogFanEnergy != null && catalogDiscountInr ? catalogFanEnergy / catalogDiscountInr : null);

  let fanEnergyRequired =
    ratePerInr != null && fanEnergyCoversInr != null ? Math.ceil(ratePerInr * fanEnergyCoversInr) : null;
  if (fanEnergyRequired == null && !isProjected) fanEnergyRequired = catalogFanEnergy;

  return {
    faceValueInr,
    discountPercent: effectivePercent,
    fanEnergyCoversInr,
    cashPayableInr,
    fanEnergyRequired,
    isProjected,
  };
}

export function formatPercent(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `${Number.isInteger(num) ? num : num.toFixed(2)}%`;
}

export function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

export function formatInr(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Like `formatInr`, but drops the trailing `.00` so pricing rows stay narrow. */
export function formatInrShort(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const fractionDigits = Number.isInteger(num) ? 0 : 2;
  return `₹${num.toLocaleString('en-IN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: 2,
  })}`;
}

/** Resolve stock from flat fields or app-shaped `inventory` on admin product rows. */
export function getProductInventory(product) {
  if (!product || typeof product !== 'object') {
    return { stockType: 'UNLIMITED', stockQuantity: null };
  }

  const inv = product.inventory;
  const stockType = String(
    product.stock_type ?? product.stockType ?? inv?.stock_type ?? inv?.stockType ?? 'UNLIMITED'
  )
    .trim()
    .toUpperCase();

  const qtyRaw =
    product.stock_quantity ?? product.stockQuantity ?? inv?.stock_quantity ?? inv?.stockQuantity;
  const stockQuantity =
    qtyRaw != null && qtyRaw !== '' && !Number.isNaN(Number(qtyRaw)) ? Number(qtyRaw) : null;

  return { stockType, stockQuantity };
}

export function isLimitedStock(product) {
  return getProductInventory(product).stockType === 'LIMITED';
}

/** Human-readable stock label; quantity is shown only for LIMITED stock. */
export function formatProductStockLabel(product) {
  const { stockType, stockQuantity } = getProductInventory(product);
  if (stockType !== 'LIMITED') return 'Unlimited';
  const qty = stockQuantity != null ? stockQuantity : '—';
  return `Limited (${qty})`;
}
